import React from "react";
import { useLeave } from "../api";
import type { LeaveType } from "../../api/leave/Leave.typed";

export type HRDataContextValue = {
  leaveTypes: LeaveType[];
  typesMap: Record<number, { Color: string; Name: string }>;
  loading: boolean;
  reload: () => Promise<void>;
};

const HRDataContext = React.createContext<HRDataContextValue | undefined>(undefined);

export function HRDataProvider(props: { children: React.ReactNode }) {
  const { listLeaveTypes } = useLeave();
  const [leaveTypes, setLeaveTypes] = React.useState<LeaveType[]>([]);
  const [loading, setLoading] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await listLeaveTypes();
      setLeaveTypes(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  }, [listLeaveTypes]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const typesMap = React.useMemo(() => {
    const map: Record<number, { Color: string; Name: string }> = {};
    leaveTypes.forEach((t) => {
      map[t.ID] = { Color: t.Color, Name: t.Name };
    });
    return map;
  }, [leaveTypes]);

  const value = React.useMemo<HRDataContextValue>(
    () => ({ leaveTypes, typesMap, loading, reload }),
    [leaveTypes, typesMap, loading, reload],
  );

  return <HRDataContext.Provider value={value}>{props.children}</HRDataContext.Provider>;
}

export function useHRData(): HRDataContextValue {
  const ctx = React.useContext(HRDataContext);
  if (!ctx) throw new Error("useHRData must be used within HRDataProvider");
  return ctx;
}
